import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { makeDeliveryOrder } from "../../../services/publicPegasApi/MakePublicOrder";
import { fetchSocialMediaLinks } from "../../../services/dashboard/SocialMediaLinksApi";
import WhatsAppDialog from "./WhatsAppDialog";

const CartDrawer = ({ 
  open, 
  onClose, 
  cart, 
  IMAGE_BASE_URL, 
  menus,
  onOrderSuccess 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";

  const [orderLoading, setOrderLoading] = useState(false);
  const [orderMsg, setOrderMsg] = useState("");
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [savedCustomerData, setSavedCustomerData] = useState(null); // NEW: حفظ بيانات العميل
  const [customerData, setCustomerData] = useState({
    customer_name: "",
    email: "",
    customer_phone1: "",
    customer_phone2: "",
    customer_phone3: "",
    customer_address1: "",
    customer_address2: "",
    customer_address3: "",
  });
  const [customerErrors, setCustomerErrors] = useState({
    customer_name: "",
    customer_phone1: "",
    customer_address1: "",
  });

  // جلب أرقام واتساب عند تحميل المكون
  useEffect(() => {
    const fetchWhatsApp = async () => {
      try {
        const response = await fetchSocialMediaLinks();
        const links = response?.data?.data || [];
        const whatsappLinks = links.filter(
          link => link.type?.toLowerCase() === "whatsapp" && link.status === "active"
        );
        setWhatsappNumbers(whatsappLinks);
      } catch (err) {
        console.error("فشل في جلب أرقام واتساب:", err);
      }
    };
    fetchWhatsApp();
  }, []);

  const handleOpenCustomerDialog = () => {
    setCustomerDialogOpen(true);
  };

  const handleCloseCustomerDialog = () => {
    setCustomerDialogOpen(false);
  };

  const handleCustomerChange = (e) => {
    setCustomerData({ ...customerData, [e.target.name]: e.target.value });
  };

  const handleAddPhone = () => {
    if (!customerData.customer_phone2) {
      setCustomerData({ ...customerData, customer_phone2: "" });
    } else if (!customerData.customer_phone3) {
      setCustomerData({ ...customerData, customer_phone3: "" });
    }
  };

  const handleRemovePhone = (field) => {
    setCustomerData({ ...customerData, [field]: "" });
  };

  const handleAddAddress = () => {
    if (!customerData.customer_address2) {
      setCustomerData({ ...customerData, customer_address2: "" });
    } else if (!customerData.customer_address3) {
      setCustomerData({ ...customerData, customer_address3: "" });
    }
  };

  const handleRemoveAddress = (field) => {
    setCustomerData({ ...customerData, [field]: "" });
  };

  const validateCustomerData = () => {
    const errors = {};
    if (!customerData.customer_name.trim()) {
      errors.customer_name = "يرجى إدخال اسم العميل";
    }
    if (
      !customerData.customer_phone1.trim() ||
      !/^\d{8,15}$/.test(customerData.customer_phone1.trim())
    ) {
      errors.customer_phone1 = "يرجى إدخال رقم هاتف صحيح (8-15 رقم)";
    }
    if (!customerData.customer_address1.trim()) {
      errors.customer_address1 = "يرجى إدخال العنوان";
    }
    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirmOrder = async () => {
    if (!cart.length) return;
    if (!validateCustomerData()) return;
    setOrderLoading(true);
    setOrderMsg("");
    try {
      const root_id = menus[0]?.root?.id;
      if (!root_id) {
        setOrderMsg("تعذر تحديد الفرع.");
        setOrderLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("customer_name", customerData.customer_name);
      formData.append("email", customerData.email);
      formData.append("customer_phone1", customerData.customer_phone1);
      formData.append("customer_phone2", customerData.customer_phone2 || "");
      formData.append("customer_phone3", customerData.customer_phone3 || "");
      formData.append("customer_address1", customerData.customer_address1);
      formData.append("customer_address2", customerData.customer_address2 || "");
      formData.append("customer_address3", customerData.customer_address3 || "");
      formData.append("root_id", root_id);

      cart.forEach((item, idx) => {
        formData.append(`items[${idx}][product_id]`, item.id);
        formData.append(`items[${idx}][price]`, item.price);
        formData.append(`items[${idx}][quantity]`, 1);
formData.append(`items[${idx}][note]`, item.note || "");
        formData.append(`items[${idx}][status]`, "pending");
      });

      // DEBUG: عرض في الكونسول البيانات المرسلة إلى makeDeliveryOrder
      try {
        const debugPayload = {};
        for (const [key, value] of formData.entries()) {
          if (Object.prototype.hasOwnProperty.call(debugPayload, key)) {
            // جمع قيم متعددة لنفس المفتاح في مصفوفة
            if (Array.isArray(debugPayload[key])) {
              debugPayload[key].push(value);
            } else {
              debugPayload[key] = [debugPayload[key], value];
            }
          } else {
            debugPayload[key] = value;
          }
        }
        console.log("makeDeliveryOrder payload (formData):", debugPayload);
        console.log("cart (raw):", cart);
        console.log("menus root_id:", root_id);
      } catch (debugErr) {
        console.warn("Failed to build debug payload:", debugErr);
      }

      const result = await makeDeliveryOrder(formData);
      const orderId = result?.data?.order_id;
      
      setOrderMsg("✅ تم إرسال الطلب بنجاح!");
      setCreatedOrderId(orderId);
      // NEW: حفظ بيانات العميل قبل مسحها
      setSavedCustomerData({
        customer_name: customerData.customer_name,
        customer_phone1: customerData.customer_phone1,
        customer_address1: customerData.customer_address1,
      });
      setCustomerDialogOpen(false);
      setCustomerData({
        customer_name: "",
        email: "",
        customer_phone1: "",
        customer_phone2: "",
        customer_phone3: "",
        customer_address1: "",
        customer_address2: "",
        customer_address3: "",
      });
      setShowSuccessDialog(true);
      onOrderSuccess();
      onClose();
    } catch (err) {
      setOrderMsg(err.message || "❌ فشل في إرسال الطلب.");
    } finally {
      setOrderLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    // عرض نافذة الواتساب إذا كان هناك أرقام واتساب ورقم طلب
    if (whatsappNumbers.length > 0 && createdOrderId) {
      setShowWhatsAppDialog(true);
    } else {
      setCreatedOrderId(null);
      setSavedCustomerData(null);
    }
  };

  const handleWhatsAppSent = () => {
    setShowWhatsAppDialog(false);
    setCreatedOrderId(null);
    setSavedCustomerData(null);
  };

  const handleWhatsAppSkip = () => {
    setShowWhatsAppDialog(false);
    setCreatedOrderId(null);
    setSavedCustomerData(null);
  };

  return (
    <>
      {/* السلة */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            bgcolor: "#fff",
            borderTopLeftRadius: { xs: 0, sm: 32 },
            borderBottomLeftRadius: { xs: 0, sm: 32 },
            boxShadow: "0 8px 32px 0 rgba(255,61,61,0.15)",
            border: "2px solid #ffb30022",
          },
        }}
      >
        <Box
          sx={{
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fffbe6",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 4 },
              pt: { xs: 2, sm: 4 },
              pb: { xs: 1.5, sm: 2 },
              borderBottom: "2px solid #ffe0b2",
              bgcolor: "#fffbe6",
              borderTopLeftRadius: { xs: 0, sm: 32 },
              borderTopRightRadius: { xs: 0, sm: 32 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: "#ff3d3d",
                letterSpacing: 2,
                fontSize: { xs: 22, sm: 26, md: 28 },
              }}
            >
              🛒 السلة
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                color: "#ff3d3d",
                bgcolor: "#fff",
                border: "2px solid #ffe0b2",
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "#ff3d3d",
                  borderColor: "#ff3d3d",
                  color: "#fff",
                  transform: "scale(1.1)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 0 }} />
          {cart.length > 0 ? (
            <List sx={{ 
              flexGrow: 1, 
              px: { xs: 1.5, sm: 2 }, 
              py: { xs: 1.5, sm: 2 }, 
              bgcolor: "#fffbe6",
              overflowY: "auto",
            }}>
              {cart.map((item, index) => (
                <ListItem
                  key={index}
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    bgcolor: "#fff",
                    borderRadius: { xs: 2, sm: 3 },
                    boxShadow: "0 2px 8px 0 #ffb30022",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: { xs: 1, sm: 1.5 },
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 1, sm: 1.5 },
                    border: "1.5px solid #ffe0b2",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                    <Box
                      sx={{
                        width: { xs: 48, sm: 54 },
                        height: { xs: 48, sm: 54 },
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 2px 8px 0 #ffb30022",
                        bgcolor: "#fff",
                        border: "1.5px solid #ffb30033",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={
                          item.image
                            ? `${IMAGE_BASE_URL}${item.image}`
                            : placeholderImage
                        }
                        alt={item.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                          display: "block",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: 14, sm: 16 },
                          color: "#181c2f",
                          mb: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#888",
                          fontWeight: 500,
                          fontSize: { xs: 12, sm: 13 },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#ffb300",
                          fontWeight: 700,
                          fontSize: { xs: 12, sm: 13 },
                          mt: 0.5,
                        }}
                      >
                        الكمية: {item.quantity || 1}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        minWidth: { xs: 60, sm: 70 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{ 
                          color: "#ffb300", 
                          fontWeight: 700, 
                          fontSize: { xs: 14, sm: 15 } 
                        }}
                      >
                        {(item.price * (item.quantity || 1)).toFixed(2)} ج.م
                      </Typography>
                    </Box>
                  </Box>
                  {item.note && (
                    <Box
                      sx={{
                        bgcolor: "#fffef5",
                        borderRadius: 1.5,
                        p: 1,
                        border: "1px solid #ffe0b2",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: 11, sm: 12 },
                          color: "#666",
                          fontStyle: "italic",
                        }}
                      >
                        📝 {item.note}
                      </Typography>
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              sx={{ 
                color: "#888", 
                mt: 6, 
                textAlign: "center", 
                fontSize: { xs: 16, sm: 18 } 
              }}
            >
              السلة فارغة
            </Typography>
          )}
          {cart.length > 0 && (
            <Box
              sx={{
                px: { xs: 2, sm: 4 },
                pb: { xs: 2, sm: 3 },
                pt: { xs: 1.5, sm: 2 },
                borderTop: "2px solid #ffe0b2",
                bgcolor: "#fffbe6",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#ffb300",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: 16, sm: 18, md: 20 },
                  borderRadius: { xs: 2, sm: 3 },
                  py: { xs: 1.2, sm: 1.5 },
                  mt: 1,
                  boxShadow: "0 2px 12px 0 rgba(255,179,0,0.13)",
                  "&:hover": { bgcolor: "#ff3d3d" },
                }}
                fullWidth
                onClick={handleOpenCustomerDialog}
                disabled={orderLoading}
              >
                {orderLoading ? "جاري إرسال الطلب..." : "✅ تأكيد الطلب"}
              </Button>
              {orderMsg && (
                <Typography
                  sx={{
                    mt: 2,
                    color: orderMsg.startsWith("✅") ? "green" : "red",
                    textAlign: "center",
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {orderMsg}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* مودال بيانات العميل */}
      <Dialog
        open={customerDialogOpen}
        onClose={handleCloseCustomerDialog}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 6 },
            bgcolor: "#fffbe6",
            boxShadow: "0 12px 48px 0 rgba(255,179,0,0.18)",
            p: 0,
            border: { xs: 0, sm: "2px solid #ffb30044" },
            overflow: "visible",
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100%", sm: "90vh" },
          },
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: -38,
              transform: "translateX(-50%)",
              bgcolor: "#fff",
              borderRadius: "50%",
              boxShadow: "0 4px 24px #ffb30022",
              width: 76,
              height: 76,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "4px solid #fffbe6",
              zIndex: 2,
            }}
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/3132/3132693.png"
              alt="logo"
              width={48}
              height={48}
              style={{ display: "block" }}
            />
          </Box>
        )}
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: "#ff3d3d",
            textAlign: "center",
            fontSize: { xs: 20, sm: 24, md: 26 },
            letterSpacing: 1,
            bgcolor: "#fff",
            borderTopLeftRadius: { xs: 0, sm: 20 },
            borderTopRightRadius: { xs: 0, sm: 20 },
            pb: 1,
            pt: { xs: 2, sm: 6 },
            px: { xs: 2, sm: 3 },
          }}
        >
          بيانات العميل
          <Typography
            sx={{ 
              color: "#888", 
              fontWeight: 400, 
              fontSize: { xs: 13, sm: 15 }, 
              mt: 0.5 
            }}
          >
            يرجى تعبئة بياناتك لإتمام الطلب
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: { xs: 1.5, sm: 2 }, 
          pb: 0, 
          px: { xs: 2, sm: 3 },
          overflowY: "auto",
        }}>
          <TextField
            label="اسم العميل"
            name="customer_name"
            value={customerData.customer_name}
            onChange={handleCustomerChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              mb: { xs: 1.5, sm: 2 },
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px #ffb30011",
              "& .MuiInputBase-root": { fontWeight: 700, fontSize: { xs: 14, sm: 16 } },
            }}
            error={!!customerErrors.customer_name}
            helperText={customerErrors.customer_name}
            InputProps={{ sx: { color: "#000" } }}
            InputLabelProps={{ sx: { color: "#000", fontSize: { xs: 14, sm: 16 } } }}
          />
          <TextField
            label="رقم الجوال 1"
            name="customer_phone1"
            value={customerData.customer_phone1}
            onChange={handleCustomerChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              mb: { xs: 1.5, sm: 2 },
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px #ffb30011",
              "& .MuiInputBase-root": { fontWeight: 700, fontSize: { xs: 14, sm: 16 } },
            }}
            error={!!customerErrors.customer_phone1}
            helperText={customerErrors.customer_phone1}
            InputProps={{ sx: { color: "#000" } }}
            InputLabelProps={{ sx: { color: "#000", fontSize: { xs: 14, sm: 16 } } }}
          />
          {/* ...existing code for additional phone fields... */}
          <TextField
            label="العنوان 1"
            name="customer_address1"
            value={customerData.customer_address1}
            onChange={handleCustomerChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              mb: { xs: 1.5, sm: 2 },
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px #ffb30011",
              "& .MuiInputBase-root": { fontWeight: 700, fontSize: { xs: 14, sm: 16 } },
            }}
            error={!!customerErrors.customer_address1}
            helperText={customerErrors.customer_address1}
            InputProps={{ sx: { color: "#000" } }}
            InputLabelProps={{ sx: { color: "#000", fontSize: { xs: 14, sm: 16 } } }}
          />
          {/* ...existing code for additional address fields... */}
          <TextField
            label="البريد الإلكتروني"
            name="email"
            value={customerData.email}
            onChange={handleCustomerChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              mb: { xs: 1.5, sm: 2 },
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px #ffb30011",
              "& .MuiInputBase-root": { fontWeight: 700, fontSize: { xs: 14, sm: 16 } },
            }}
            InputProps={{ sx: { color: "#000" } }}
            InputLabelProps={{ sx: { color: "#000", fontSize: { xs: 14, sm: 16 } } }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2 },
            pt: { xs: 1, sm: 0 },
            bgcolor: "#fff",
            borderBottomLeftRadius: { xs: 0, sm: 20 },
            borderBottomRightRadius: { xs: 0, sm: 20 },
            gap: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
          }}
        >
          <Button
            onClick={handleCloseCustomerDialog}
            color="secondary"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#eee",
              color: "#ff3d3d",
              px: { xs: 2, sm: 3 },
              fontSize: { xs: 14, sm: 15 },
              "&:hover": { bgcolor: "#ffb300", color: "#fff" },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmOrder}
            color="primary"
            variant="contained"
            disabled={orderLoading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{
              fontWeight: 900,
              borderRadius: 2,
              bgcolor: "#ffb300",
              color: "#fff",
              px: { xs: 2, sm: 4 },
              fontSize: { xs: 14, sm: 15 },
              "&:hover": { bgcolor: "#ff3d3d" },
              boxShadow: "0 2px 8px #ffb30033",
            }}
          >
            {orderLoading ? "جاري الإرسال..." : "تأكيد الطلب"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة نجاح */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 6 },
            bgcolor: "#fff",
            boxShadow: "0 12px 48px 0 rgba(76, 175, 80, 0.18)",
            p: 0,
            border: { xs: 0, sm: "2px solid #4caf50" },
            overflow: "visible",
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: -38,
              transform: "translateX(-50%)",
              bgcolor: "#fff",
              borderRadius: "50%",
              boxShadow: "0 4px 24px #4caf5022",
              width: 76,
              height: 76,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "4px solid #fff",
              zIndex: 2,
            }}
          >
            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 54 }} />
          </Box>
        )}
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: "#4caf50",
            textAlign: "center",
            fontSize: { xs: 20, sm: 24, md: 26 },
            letterSpacing: 1,
            bgcolor: "#fff",
            borderTopLeftRadius: { xs: 0, sm: 20 },
            borderTopRightRadius: { xs: 0, sm: 20 },
            pb: 1,
            pt: { xs: 3, sm: 6 },
            px: { xs: 2, sm: 3 },
          }}
        >
          {isMobile && (
            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 64, mb: 2 }} />
          )}
          تم إرسال الطلب بنجاح!
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ 
            color: "#333", 
            fontWeight: 500, 
            fontSize: { xs: 15, sm: 17 }, 
            textAlign: "center", 
            mb: 2,
            lineHeight: 1.6,
          }}>
            شكراً لك على طلبك.<br />
            سيتم التواصل معك قريباً لتأكيد الطلب والتوصيل.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 2 }, 
          pt: 0, 
          bgcolor: "#fff", 
          borderBottomLeftRadius: { xs: 0, sm: 20 }, 
          borderBottomRightRadius: { xs: 0, sm: 20 } 
        }}>
          <Button
            onClick={handleSuccessDialogClose}
            color="primary"
            variant="contained"
            fullWidth
            size={isMobile ? "large" : "large"}
            sx={{
              fontWeight: 900,
              borderRadius: 2,
              bgcolor: "#4caf50",
              color: "#fff",
              px: 4,
              py: { xs: 1.2, sm: 1 },
              fontSize: { xs: 16, sm: 15 },
              "&:hover": { bgcolor: "#388e3c" },
              boxShadow: "0 2px 8px #4caf5033",
            }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة تأكيد إرسال للواتساب */}
      <WhatsAppDialog
        open={showWhatsAppDialog}


        
        onClose={handleWhatsAppSkip}
        whatsappNumbers={whatsappNumbers}
        orderId={createdOrderId}
        customerData={savedCustomerData}
        onSendToWhatsApp={handleWhatsAppSent}
      />
    </>
  );
};

export default CartDrawer;
